import React, { useEffect, useMemo, useState } from 'react';
import { adminService } from '../../_services/admin.service';

const salonName = (s) => s.salonName || s.name || 'Unnamed business';
const ownerIdFor = (s) => {
    const owner = s.salonOwner || s.owner;
    return owner && typeof owner === 'object' ? owner._id : owner;
};

/** Admin-only, lossless linker for existing salons (Cropshop rollout tool), and its undo: unlink one salon. */
export default function MultiLocationLinkModal({ onClose }) {
    const [mode, setMode] = useState('link'); // 'link' | 'unlink'
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState([]);
    const [ownerUserId, setOwnerUserId] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(null);

    useEffect(() => {
        let live = true;
        const timer = setTimeout(async () => {
            if (search.trim().length < 2) { setResults([]); return; }
            setLoading(true); setError('');
            try {
                const response = await adminService.getSalons({ search: search.trim(), limit: 50, claimedFilter: 'claimed' });
                if (live) setResults(response?.responseData?.salons || []);
            } catch (e) {
                if (live) setError('Could not search businesses.');
            } finally { if (live) setLoading(false); }
        }, 250);
        return () => { live = false; clearTimeout(timer); };
    }, [search]);

    const ownerCandidates = useMemo(() => {
        const seen = new Set();
        return selected.map((s) => ({ id: ownerIdFor(s), label: `${salonName(s)} owner` }))
            .filter((o) => o.id && !seen.has(String(o.id)) && seen.add(String(o.id)));
    }, [selected]);

    useEffect(() => {
        if (!ownerUserId && ownerCandidates.length === 1) setOwnerUserId(String(ownerCandidates[0].id));
    }, [ownerCandidates, ownerUserId]);

    const toggle = (salon) => setSelected((prev) => mode === 'unlink'
        ? (prev.some((s) => s._id === salon._id) ? [] : [salon])
        : prev.some((s) => s._id === salon._id)
            ? prev.filter((s) => s._id !== salon._id)
            : [...prev, salon]);
    const switchMode = (m) => { if (saving) return; setMode(m); setSelected([]); setError(''); setDone(null); };

    const unlink = async () => {
        const salon = selected[0];
        if (!salon) return;
        if (!window.confirm(`Take ${salonName(salon)} out of its location group? Its owner, bookings, clients and money stay as they are. People who only reached it through the group lose access, and it comes off the group owner's plan.`)) return;
        setSaving(true); setError('');
        try {
            const response = await adminService.unlinkMultiLocation(salon._id);
            if (response?.statusCode !== 1) throw new Error(response?.error?.responseMessage || 'Unlink failed');
            setDone({ unlinked: true, salon: salonName(salon), ...(response.responseData || {}) });
        } catch (e) {
            setError(e?.responseMessage || e?.message || String(e) || 'Could not unlink this location.');
        } finally { setSaving(false); }
    };

    const link = async () => {
        if (selected.length < 2 || !ownerUserId.trim() || !name.trim()) return;
        // This creates only additive group/membership/staff rows; it does not
        // change the existing site owners or move money.
        if (!window.confirm(`Link ${selected.length} existing businesses as “${name.trim()}”? Existing owners and payout routing stay unchanged.`)) return;
        setSaving(true); setError('');
        try {
            const response = await adminService.linkMultiLocation({
                ownerUserId: ownerUserId.trim(), name: name.trim(), salonIds: selected.map((s) => s._id)
            });
            if (response?.statusCode !== 1) throw new Error(response?.error?.responseMessage || 'Link failed');
            setDone(response.responseData);
        } catch (e) {
            setError(e?.responseMessage || e?.message || String(e) || 'Could not link these locations.');
        } finally { setSaving(false); }
    };

    return (
        <div role="dialog" aria-modal="true" aria-label="Link existing locations" style={{ position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,.5)', display: 'grid', placeItems: 'center', padding: 20 }} onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <div style={{ width: 'min(620px, 100%)', maxHeight: '88vh', overflow: 'auto', background: '#fff', borderRadius: 14, padding: 28 }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                    {[['link', 'Link locations'], ['unlink', 'Unlink a location']].map(([m, label]) => (
                        <button key={m} type="button" onClick={() => switchMode(m)} disabled={saving}
                            className={`admin-btn ${mode === m ? 'admin-btn-primary' : 'admin-btn-secondary'}`} style={{ flex: 1 }}>{label}</button>
                    ))}
                </div>
                <h2 style={{ margin: '0 0 8px' }}>{mode === 'unlink' ? 'Unlink a location' : 'Link existing locations'}</h2>
                <p style={{ margin: '0 0 22px', color: '#666', fontSize: 14 }}>{mode === 'unlink'
                    ? 'Takes one business out of its location group. Its owner, payout account, bookings, clients and history stay as they are. Shared marketing credits stay with the group.'
                    : 'Creates one location group without changing site ownership, payout accounts or historical data. Gift cards across locations and shared clients start off; the owner can switch them on in Settings, Locations.'}</p>

                {done && done.unlinked ? (
                    <>
                        <div style={{ padding: 14, borderRadius: 9, background: done.modified ? '#E8F5E9' : '#FFF8E1', color: done.modified ? '#2E7D32' : '#8D6E00' }}>
                            {done.modified
                                ? `${done.salon} is no longer part of its group.${(done.ownerRecordsKept || []).length ? ' The group owner keeps a staff record there because they have bookings at it.' : ''}`
                                : `${done.salon} wasn't in a location group, so nothing changed.`}
                        </div>
                        <button className="admin-btn admin-btn-primary" onClick={onClose} style={{ width: '100%', marginTop: 18 }}>Done</button>
                    </>
                ) : done ? (
                    <>
                        <div style={{ padding: 14, borderRadius: 9, background: '#E8F5E9', color: '#2E7D32' }}>Linked {selected.length} locations successfully.</div>
                        <button className="admin-btn admin-btn-primary" onClick={onClose} style={{ width: '100%', marginTop: 18 }}>Done</button>
                    </>
                ) : (
                    <>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Find businesses</label>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search Cropshop, Norwich…" className="admin-search-input" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />
                        <div style={{ border: '1px solid #E4E4E7', borderRadius: 9, maxHeight: 220, overflow: 'auto', marginBottom: 18 }}>
                            {loading && <p style={{ padding: 14, margin: 0, color: '#777' }}>Searching…</p>}
                            {!loading && search.trim().length >= 2 && !results.length && <p style={{ padding: 14, margin: 0, color: '#777' }}>No claimed businesses found.</p>}
                            {results.map((salon) => (
                                <label key={salon._id} style={{ display: 'flex', gap: 10, padding: '11px 13px', borderBottom: '1px solid #F1F1F3', cursor: 'pointer' }}>
                                    <input type={mode === 'unlink' ? 'radio' : 'checkbox'} name="ml-salon" checked={selected.some((s) => s._id === salon._id)} onChange={() => toggle(salon)} />
                                    <span><strong style={{ display: 'block', fontSize: 13 }}>{salonName(salon)}</strong><small style={{ color: '#777' }}>{salon.formattedAddress || salon._id}</small></span>
                                </label>
                            ))}
                        </div>

                        {selected.length > 0 && <p style={{ margin: '0 0 14px', fontSize: 13, color: '#4B4B52' }}><strong>{selected.length} selected:</strong> {selected.map(salonName).join(', ')}</p>}

                        {mode === 'link' && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Group name
                                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Cropshop" style={{ width: '100%', marginTop: 6, padding: '10px 11px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' }} />
                            </label>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Owner login
                                {ownerCandidates.length ? (
                                    <select value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} style={{ width: '100%', marginTop: 6, padding: '10px 11px', border: '1px solid #ddd', borderRadius: 8 }}>
                                        <option value="">Choose owner</option>
                                        {ownerCandidates.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                    </select>
                                ) : (
                                    <input value={ownerUserId} onChange={(e) => setOwnerUserId(e.target.value)} placeholder="User ID" style={{ width: '100%', marginTop: 6, padding: '10px 11px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box' }} />
                                )}
                            </label>
                        </div>}

                        {error && <p style={{ color: '#C62828', fontSize: 13, margin: '14px 0 0' }}>{error}</p>}
                        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                            <button className="admin-btn admin-btn-secondary" onClick={onClose} disabled={saving} style={{ flex: 1 }}>Cancel</button>
                            {mode === 'unlink'
                                ? <button className="admin-btn admin-btn-primary" onClick={unlink} disabled={saving || selected.length !== 1} style={{ flex: 1 }}>{saving ? 'Unlinking…' : 'Unlink location'}</button>
                                : <button className="admin-btn admin-btn-primary" onClick={link} disabled={saving || selected.length < 2 || !ownerUserId.trim() || !name.trim()} style={{ flex: 1 }}>{saving ? 'Linking…' : 'Link locations'}</button>}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
